import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { connect } from 'react-redux';
import { useSiteContext } from '@eti/providers';
import ReceiptPage from '../components/ReceiptPage';
import { CREATING_ORDER_FLAG, POST_BOOKING_ORDER_FLAG } from '../../../constants/receiptConstants';
import {
  getAirTripType,
  getBounds,
  getDestination,
  getExtraProducts,
  getGASegmentItems,
  getNumberOfTravelers,
  getOrigin,
  getSegments,
  getTravelersDisplayPrices,
  getTravelersWithExtraProducts,
} from '../../../utils/format';
import { addLegendNumberToSegmentEvents } from '../../../widgets/trip-details/utils/format';
import ErrorPage from '../../status-pages/components/ErrorPage';
import Loading from '../../../common/loading/components/Loading';
import { generateInitialValuesAncillaryBundles } from '../../traveler-details/utils/bundles';
import { useJson } from '../../../common/custom-hooks/json';
import { endpoints } from '../../../constants/routesAndApisConstants';
import { ORDER_QUERY } from '../graphql/queries';

const mapReceiptProps = (data, siteContext) => {
  // TODO remove this once the data is normalized on IBE: https://jira.etraveli.net/browse/WEB-1981
  const { siteCurrency } = siteContext;
  const { code: currency, exponent } = siteCurrency;

  const { orderNew } = data;

  const {
    addOnOrders,
    chargeCurrencyPriceFormat,
    customerDetails,
    instalmentsTotalFee,
    priceInChargeCurrency,
    purchaseDate,
    shouldAddInstallmentFeeInTotalAmount,
    totalPriceIncludingAddOnOrders,
  } = orderNew;
  const trip = orderNew.trips[0];
  const bounds = getBounds(orderNew);
  const segments = getSegments(orderNew).filter((segment) => segment.__typename === 'TripSegment');
  const items = getGASegmentItems(segments);
  const extraProducts = getExtraProducts(trip.extraProducts);
  const { bundles } = generateInitialValuesAncillaryBundles(trip.ancillaryBundles);

  const travelersWithExtraProducts = getTravelersWithExtraProducts(trip.travelers, extraProducts);
  const travelersDisplayPrices = getTravelersDisplayPrices(trip.travelers, trip.travelerPrices);
  const { nrOfAdults, nrOfChildren, nrOfInfants } = getNumberOfTravelers(trip.travelers);
  const airTripType = getAirTripType(trip);

  const totalPrice =
    shouldAddInstallmentFeeInTotalAmount && instalmentsTotalFee
      ? orderNew.totalPrice + instalmentsTotalFee
      : orderNew.totalPrice;

  const isCreatingOrder = sessionStorage.getItem(CREATING_ORDER_FLAG);
  if (isCreatingOrder) {
    sessionStorage.removeItem(CREATING_ORDER_FLAG);
  }

  const isPostBookingOrder = sessionStorage.getItem(POST_BOOKING_ORDER_FLAG);
  if (isPostBookingOrder) {
    sessionStorage.removeItem(POST_BOOKING_ORDER_FLAG);
  }

  const discountCode = sessionStorage.getItem('discountCode');
  if (discountCode) {
    sessionStorage.removeItem('discountCode');
  }

  const isOtherChargeCurrency =
    priceInChargeCurrency && priceInChargeCurrency?.price?.currency?.code !== currency;

  const gaData = {
    items,
    nrOfAdults,
    nrOfChildren,
    nrOfInfants,
    orderNumber: orderNew.orderNumber,
    productPrices: extraProducts.map((product) => product.totalPrice / 10 ** exponent),
    products: extraProducts.map(({ code }) => code),
    totalAmount: totalPrice / 10 ** exponent,
    airTripType,
    orderState: orderNew.isRefunded ? 1 : 0,
    ...{ ...(isCreatingOrder && { orderCreated: 1 }) },
  };

  const gfData = {
    send_to: window.spa_variables.googleFlightsPartnerId,
    trip_type: airTripType,
    value: (totalPrice / 10 ** exponent).toString(),
    currency,
    passengers: {
      total: nrOfAdults + nrOfChildren + nrOfInfants,
      adult: nrOfAdults,
      child: nrOfChildren,
      infant_in_lap: nrOfInfants,
      infant_in_seat: 0,
    },
    items,
  };

  let cancellationMessage = null;
  if (orderNew.orderInformation && orderNew.orderInformation.length > 0) {
    const { header, text } = orderNew.orderInformation[0];
    cancellationMessage = {
      header,
      text,
    };
  }

  return {
    addOnOrders,
    baggageInfoUrl: data.links.baggageInformation,
    bounds: addLegendNumberToSegmentEvents(bounds),
    bundles,
    cancellationMessage,
    cc2carrierBreakdown: orderNew?.transactionBreakdown ?? null,
    chargeCurrencyPriceFormat,
    checkedBaggageSummary: trip.checkedBaggageSummary,
    customerDetails,
    destination: getDestination(bounds),
    discountPrice: orderNew?.discountPrice?.price?.value ?? null,
    shouldDisplayNotification: orderNew.hasBeenChanged,
    fareRules: trip.fareRules,
    flightInformation: orderNew.flightInformation,
    gaData,
    gfData,
    instalmentsTotalFee,
    isCreatingOrder,
    isOtherChargeCurrency,
    isPostBookingOrder,
    isMultiStop: trip.type !== 'ONE_WAY' && trip.type !== 'RETURN',
    isUpsellAllowed: orderNew.isUpsellAllowed,
    oldOrderNumber: orderNew?.oldOrderNumber,
    orderExtraProducts: extraProducts,
    orderNumber: orderNew.orderNumber,
    obfuscatedOrderReference: orderNew.obfuscatedOrderReference,
    orderReferenceSerialized: orderNew.orderReferenceSerialized,
    origin: getOrigin(bounds),
    paymentTypes: orderNew.paymentProducts,
    postBookingUrl: orderNew.postBookingLink,
    priceInChargeCurrency: priceInChargeCurrency?.price?.value,
    purchaseDate,
    segments,
    travelDocUrl: orderNew.travelDocLink,
    travelers: travelersWithExtraProducts,
    travelerPrices: travelersDisplayPrices,
    tripCharacteristics: trip.tripCharacteristics,
    tripProviderSystems: trip.systems,
    totalPrice,
    totalPriceIncludingAddOnOrders,
  };
};

const ReceiptContainer = () => {
  const { data, loading, error } = useQuery(ORDER_QUERY, { fetchPolicy: 'no-cache' });
  const siteContext = useSiteContext();
  const { data: textData } = useJson(endpoints.ORDERPAGE_GET_TEXTS);
  const { model: texts } = textData || {};
  const receiptProps = useMemo(
    () => data && mapReceiptProps(data, siteContext),
    [data, siteContext],
  );

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <ErrorPage />;
  }
  return (
    <ReceiptPage
      texts={texts}
      {...receiptProps}
      paymentProducts={data?.orderNew?.paymentProducts}
    />
  );
};

export default connect()(ReceiptContainer);
